import React, { useState, useEffect } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import {
  DoorOpen, DoorClosed,
  MoreVertical,
  LogOut,
  Plus,
  PanelRightOpen,
} from "lucide-react";
import ChatInterface from "./components/ChatInterface";
import ConversationSidebar from "./components/ConversationSidebar";
import Settings from "./components/Settings/Settings";
import ROIDashboard from "./components/ROIDashboard";
import AutomationDashboard from "./components/AutomationDashboard";
import AffiliateDashboard from "./components/Affiliate/AffiliateDashboard";
import AffiliateOverlay from "./components/Affiliate/AffiliateOverlay";
import AuthPage from "./components/Auth/AuthPage";
import AuthPreview from "./pages/AuthPreview";
import Legal from "./pages/Legal";
import CustomDashboard from "./components/CustomDashboard/CustomDashboard";
import CustomMenu from "./components/CustomMenu";
import UserMenu from "./components/UserMenu";
import InstallPrompt from "./components/InstallPrompt";
import OnboardingFlow, { useOnboarding } from "./components/Onboarding/OnboardingFlow";
import VoiceReplySpeaker from "./components/VoiceReplySpeaker";
import TierProvisioningBanner from "./components/TierProvisioningBanner";
// Command Center (JJ's flag-gated shell) — lazy chunks, NOT static imports.
// The flags default OFF, so statically importing this tree shipped ~25
// components + its CSS to every user who never sees it. Lazy keeps the main
// bundle clean; the chunk loads only when command_center_ui is actually on.
const GlobalRail = React.lazy(() => import("./components/CommandCenter/GlobalRail"));
const OperationsWorkspace = React.lazy(() => import("./components/CommandCenter/OperationsWorkspace"));
const CommandContextPanel = React.lazy(() => import("./components/CommandCenter/CommandContextPanel"));
const CommandHeader = React.lazy(() => import("./components/CommandCenter/CommandHeader"));
import { authFetch, apiUrl } from "./utils/api";
import {
  conversationApi,
  sanitizeConversationMessages,
} from "./adapters/conversationApi";
import { reduceExecutionEvent } from "./adapters/executionEventReducer";
import { isCommandCenterChildEnabled } from "./adapters/commandCenterFlags";
import {
  readCommandCenterRoute,
  writeCommandCenterRoute,
} from "./adapters/commandCenterRoute";
import {
  createInitialExecutionState,
  normalizedExecutionReducer,
  selectExecutionPresentation,
} from "./adapters/normalizedExecution";
import {
  createInitialObjectiveOutcomeState,
  createPersistedObjective,
  objectiveOutcomeReducer,
  selectObjectiveLabel,
  selectObjectiveOutcomePresentation,
} from "./adapters/objectiveOutcome";
import {
  approvalAttentionReducer,
  createInitialApprovalAttentionState,
  selectApprovalAttentionPresentation,
} from "./adapters/approvalAttention";
import {
  resolveCommandDestination,
  selectCommandDestinations,
  selectReusableResultPresentation,
} from "./adapters/destinationReuse";
import { recordCommandCenterDiagnostic } from "./adapters/commandCenterDiagnostics";
import { cancelChat, postChat, readChatSSE } from "./services/chatTransport";
import { useFeatureFlag, FLAGS } from "./config/featureFlags";
import "./App.css";
import "./styles/liquid-glass.css";

// Note: This app now has unified authentication
// User logs in ONCE for entire Optimus OS (chat, automations, ROI, affiliate)
// Auth is global, not siloed to affiliate

function App() {
  const { signOut } = useClerk();
  const { user: clerkUser, isLoaded: clerkLoaded, isSignedIn } = useUser();

  // Preview Mode - access via ?preview=auth or ?preview=legal in URL
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("preview") === "auth") {
    return <AuthPreview />;
  }
  if (urlParams.get("preview") === "legal") {
    return <Legal />;
  }
  // Legal routes — public, no auth needed. Required by Google OAuth
  // branding verification (home page must LINK to a privacy policy that
  // actually renders privacy content when clicked).
  const pathname = window.location.pathname;
  if (
    pathname === "/privacy" ||
    pathname === "/privacy-policy" ||
    pathname === "/terms" ||
    pathname === "/terms-of-service" ||
    pathname === "/legal"
  ) {
    return <Legal initialTab={pathname.startsWith("/privacy") ? "privacy" : "terms"} />;
  }

  const [messages, setMessages] = useState([]);
  const [conversationLoadState, setConversationLoadState] = useState({
    status: "idle",
    conversationId: null,
    error: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState([]); // MESSAGE_QUEUEING: Track queued messages
  const [showSettings, setShowSettings] = useState(false);
  // Claude-connect nudge (BYOK: subscribers run on their OWN Claude OAuth —
  // first message fails without it, and onboarding alone wasn't enough).
  // null = unknown/loading, false = not connected, true = connected.
  const [claudeConnected, setClaudeConnected] = useState(null);
  const [showROIDashboard, setShowROIDashboard] = useState(false);
  const [showAutomations, setShowAutomations] = useState(false);
  const [showAffiliate, setShowAffiliate] = useState(false);
  const [showCustomDashboard, setShowCustomDashboard] = useState(false);
  // Settings can be opened straight onto a tab (the Custom menu's backdoor)
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);
  const openSettingsTab = (tab, anchor) => { setSettingsInitialTab(anchor ? `${tab}#${anchor}` : tab); setShowSettings(true); };
  const [ghlConnected, setGhlConnected] = useState(false);
  const [apiStatus, setApiStatus] = useState("disconnected");
  const [currentProvider, setCurrentProvider] = useState(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showSplashPage, setShowSplashPage] = useState(false);

  // Startup sequence state
  const [startupComplete, setStartupComplete] = useState(false);
  const [startupStatus, setStartupStatus] = useState("Initializing...");
  const [startupProgress, setStartupProgress] = useState(0);

  // Affiliate overlay state
  const [showAffiliateOverlay, setShowAffiliateOverlay] = useState(false);
  const [overlayTrigger, setOverlayTrigger] = useState(null);

  const [currentConversationId, setCurrentConversationId] = useState(
    sessionStorage.getItem("currentConversationId") || null,
  );
  const [eventSource, setEventSource] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showConversationSidebar, setShowConversationSidebar] = useState(true);
  const [abortController, setAbortController] = useState(null);
  // Honey Badger state
  const [honeyBadgerActive, setHoneyBadgerActive] = useState(false);
  const [honeyBadgerTarget, setHoneyBadgerTarget] = useState("");
  const [honeyBadgerProgress, setHoneyBadgerProgress] = useState("0/0");
  const [honeyBadgerComplete, setHoneyBadgerComplete] = useState(false);
  // Track if Harry is active (persists across status message changes)
  const [isHarryActive, setIsHarryActive] = useState(false);
  // Initialize collapsed state based on screen size (default collapsed on mobile)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  // Sidebar mode: "chats" or "files" (for VFS file browser)
  const [sidebarMode, setSidebarMode] = useState("chats");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Text to insert into chat (from FileBrowser "Use in Chat")
  const [chatInsertText, setChatInsertText] = useState(null);
  const [agentStatus, setAgentStatus] = useState("");
  const [progressMessages, setProgressMessages] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Streaming text state - for progressive text rendering (like Claude Code)
  // When streaming_text_updates is ON, this holds contentBlocks array for interleaved rendering
  const [streamingMessage, setStreamingMessage] = useState(null);
  // Ref to track contentBlocks for final message conversion (avoids React state timing issues)
  const streamingContentBlocksRef = React.useRef(null);
  const conversationLoadRequestRef = React.useRef(0);
  const harryHadActiveTaskRef = React.useRef(false);
  const commandCenterExposureRef = React.useRef(null);
  const commandCenterTerminalRef = React.useRef(null);
  // Legacy tool tracking (used when streaming OFF)
  const [activeTools, setActiveTools] = useState([]);
  const [completedTools, setCompletedTools] = useState([]);
  const [normalizedExecution, dispatchNormalizedExecution] = React.useReducer(
    normalizedExecutionReducer,
    currentConversationId,
    createInitialExecutionState,
  );
  const [objectiveOutcome, dispatchObjectiveOutcome] = React.useReducer(
    objectiveOutcomeReducer,
    currentConversationId,
    createInitialObjectiveOutcomeState,
  );
  const [approvalAttention, dispatchApprovalAttention] = React.useReducer(
    approvalAttentionReducer,
    currentConversationId,
    createInitialApprovalAttentionState,
  );

  // Feature flags
  const streamingTextEnabled = useFeatureFlag(FLAGS.STREAMING_TEXT_UPDATES);
  const streamContainmentEnabled = useFeatureFlag(FLAGS.STREAM_CONTAINMENT);
  const automationsEnabled = useFeatureFlag(FLAGS.AUTOMATIONS);
  const affiliatesEnabled = useFeatureFlag(FLAGS.AFFILIATES);
  const customDashboardsEnabled = useFeatureFlag(FLAGS.CUSTOM_DASHBOARDS);
  const messageQueueingEnabled = useFeatureFlag(FLAGS.MESSAGE_QUEUEING);
  const liquidGlassEnabled = useFeatureFlag(FLAGS.LIQUID_GLASS);
  const commandCenterEnabled = useFeatureFlag(FLAGS.COMMAND_CENTER_UI);
  const commandCenterChatFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_CHAT,
  );
  const commandCenterChatEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterChatFlagEnabled,
  );
  const commandCenterExecutionFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_EXECUTION,
  );
  const commandCenterExecutionEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterExecutionFlagEnabled,
  );
  const commandCenterObjectivesFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_OBJECTIVES,
  );
  const commandCenterObjectivesEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterObjectivesFlagEnabled,
  );
  const commandCenterApprovalsFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_APPROVALS,
  );
  const commandCenterApprovalsEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterApprovalsFlagEnabled,
  );
  const commandCenterAttentionFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_ATTENTION,
  );
  const commandCenterAttentionEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterAttentionFlagEnabled,
  );
  const commandCenterDestinationsFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_DESTINATIONS,
  );
  const commandCenterDestinationsEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterDestinationsFlagEnabled,
  );
  const commandCenterReusableResultsFlagEnabled = useFeatureFlag(
    FLAGS.COMMAND_CENTER_REUSABLE_RESULTS,
  );
  const commandCenterReusableResultsEnabled = isCommandCenterChildEnabled(
    commandCenterEnabled,
    commandCenterReusableResultsFlagEnabled,
  );
  const commandExecutionPresentation = React.useMemo(
    () => selectExecutionPresentation(normalizedExecution, { isOnline }),
    [normalizedExecution, isOnline],
  );
  const reduceStreamingMessageForEvent = (previous, event) => {
    const next = reduceExecutionEvent(
      { streamingMessage: previous },
      event,
      {
        streamingTextEnabled,
        containStreamedText: streamContainmentEnabled,
      },
    ).streamingMessage;
    streamingContentBlocksRef.current = next?.contentBlocks || null;
    return next;
  };
  // URL state is intentionally ignored until the parent shell and destination
  // child flag are both effective. This preserves byte-for-byte legacy
  // behavior during a kill-switch rollback, even if a stale deep link remains.
  const [commandDestination, setCommandDestination] = useState("command");
  const [commandContextOpen, setCommandContextOpen] = useState(true);
  const currentObjective = React.useMemo(() => {
    const latestUserMessage = [...messages]
      .reverse()
      .find((message) => message.role === "user");
    const rawContent = latestUserMessage?.content;
    const content = typeof rawContent === "string"
      ? rawContent
      : rawContent?.text || rawContent?.content || "";
    const normalized = String(content).replace(/\s+/g, " ").trim();

    if (!normalized) return "Start a new command";
    return normalized.length > 78 ? `${normalized.slice(0, 75)}...` : normalized;
  }, [messages]);
  const displayedObjective = commandCenterObjectivesEnabled
    ? selectObjectiveLabel(objectiveOutcome, currentObjective)
    : currentObjective;
  const objectiveOutcomePresentation = React.useMemo(
    () => selectObjectiveOutcomePresentation(objectiveOutcome),
    [objectiveOutcome],
  );
  const approvalAttentionPresentation = React.useMemo(
    () => selectApprovalAttentionPresentation(approvalAttention),
    [approvalAttention],
  );
  const commandDestinations = React.useMemo(
    () => selectCommandDestinations({ automationsEnabled }),
    [automationsEnabled],
  );
  const handleUseResult = React.useCallback((summary) => {
    setChatInsertText(summary);
    setShowAutomations(false);
    setShowSettings(false);
    setSidebarMode("chats");
    setCommandDestination("command");
    if (commandCenterEnabled && commandCenterDestinationsEnabled) {
      writeCommandCenterRoute({ destination: "command" });
    }
    if (window.innerWidth <= 1180) setSidebarCollapsed(true);
    recordCommandCenterDiagnostic("reuse_invoked");
  }, [commandCenterEnabled, commandCenterDestinationsEnabled]);
  const reusableOutcomePresentation = React.useMemo(
    () => selectReusableResultPresentation(
      objectiveOutcomePresentation.outcome,
      { onUseResult: handleUseResult },
    ),
    [objectiveOutcomePresentation.outcome, handleUseResult],
  );

  React.useEffect(() => {
    if (!["completed", "failed", "stopped"].includes(normalizedExecution.phase)) return;
    if (commandCenterEnabled) {
      const terminalSignature = `${normalizedExecution.executionId || "execution"}:${normalizedExecution.phase}`;
      if (commandCenterTerminalRef.current !== terminalSignature) {
        commandCenterTerminalRef.current = terminalSignature;
        recordCommandCenterDiagnostic("task_terminal", {
          phase: normalizedExecution.phase,
        });
      }
    }
    dispatchObjectiveOutcome({
      type: "objective/execution_terminal",
      execution: normalizedExecution,
    });
  }, [normalizedExecution, commandCenterEnabled]);
  React.useEffect(() => {
    if (!commandCenterEnabled) return;
    const snapshot = {
      chat: commandCenterChatEnabled,
      execution: commandCenterExecutionEnabled,
      objectives: commandCenterObjectivesEnabled,
      approvals: commandCenterApprovalsEnabled,
      attention: commandCenterAttentionEnabled,
      destinations: commandCenterDestinationsEnabled,
      reusableResults: commandCenterReusableResultsEnabled,
    };
    const signature = JSON.stringify(snapshot);
    if (commandCenterExposureRef.current === signature) return;
    commandCenterExposureRef.current = signature;
    recordCommandCenterDiagnostic("shell_exposed", snapshot);
  }, [
    commandCenterEnabled,
    commandCenterChatEnabled,
    commandCenterExecutionEnabled,
    commandCenterObjectivesEnabled,
    commandCenterApprovalsEnabled,
    commandCenterAttentionEnabled,
    commandCenterDestinationsEnabled,
    commandCenterReusableResultsEnabled,
  ]);
  const commandExecutionTools = React.useMemo(() => {
    const streamTools = (streamingMessage?.contentBlocks || []).filter(
      (block) => block.type === "tool",
    );
    return {
      active: [
        ...activeTools,
        ...streamTools.filter((tool) =>
          ["queued", "running"].includes(tool.status),
        ),
      ],
      completed: [
        ...completedTools,
        ...streamTools.filter((tool) =>
          ["success", "failed"].includes(tool.status),
        ),
      ],
    };
  }, [activeTools, completedTools, streamingMessage]);

  const handleCommandNavigation = React.useCallback((destination, options = {}) => {
    const resolvedDestination = commandCenterDestinationsEnabled
      ? resolveCommandDestination(destination, { automationsEnabled })
      : destination;
    if (!resolvedDestination) return;

    const activateDestination = () => {
      setCommandDestination(resolvedDestination);
      if (options.updateHistory !== false) {
        writeCommandCenterRoute({ destination: resolvedDestination });
      }
      recordCommandCenterDiagnostic("destination_opened", {
        destination: resolvedDestination,
      });
    };

    if (resolvedDestination === "command") {
      setShowAutomations(false);
      setShowSettings(false);
      setSidebarMode("chats");
      activateDestination();
      return;
    }
    if (resolvedDestination === "operations") {
      if (!automationsEnabled) return;
      setShowSettings(false);
      setSidebarMode("chats");
      setShowAutomations(false);
      activateDestination();
      return;
    }
    if (resolvedDestination === "library") {
      setShowAutomations(false);
      setShowSettings(false);
      setSidebarMode("files");
      setSidebarCollapsed(false);
      activateDestination();
      return;
    }
    if (resolvedDestination === "connections") {
      setShowAutomations(false);
      setSidebarMode("chats");
      setShowSettings(true);
      activateDestination();
    }
  }, [commandCenterDestinationsEnabled, automationsEnabled]);

  React.useEffect(() => {
    if (!commandCenterEnabled || !commandCenterDestinationsEnabled) return undefined;

    const syncFromLocation = () => {
      const availableDestinations = commandDestinations.map(({ id }) => id);
      const route = readCommandCenterRoute(window.location, {
        allowedDestinations: availableDestinations,
      });
      handleCommandNavigation(route.destination, { updateHistory: false });
    };

    syncFromLocation();
    window.addEventListener("popstate", syncFromLocation);
    return () => window.removeEventListener("popstate", syncFromLocation);
  }, [
    commandCenterEnabled,
    commandCenterDestinationsEnabled,
    commandDestinations,
    handleCommandNavigation,
  ]);

  const toggleConversationLibrary = () => {
    const willOpen = sidebarCollapsed;
    setSidebarCollapsed(!sidebarCollapsed);
    if (willOpen && window.innerWidth <= 1180) setCommandContextOpen(false);
  };

  const openCommandContext = React.useCallback(() => {
    setCommandContextOpen(true);
    if (window.innerWidth <= 1180) setSidebarCollapsed(true);
    recordCommandCenterDiagnostic("context_opened");
  }, []);

  const closeCommandContext = React.useCallback(() => {
    setCommandContextOpen(false);
    recordCommandCenterDiagnostic("context_closed");
  }, []);

  const openCommandAccount = React.useCallback(() => setShowSettings(true), []);

  // Onboarding state - shows tutorial on first login, can be replayed from Settings
  const {
    shouldShow: showOnboarding,
    isReplay: isOnboardingReplay,
    showOnboarding: triggerOnboarding,
    hideOnboarding,
  } = useOnboarding();

  // Liquid Glass: Apply data attribute when feature flag is enabled
  useEffect(() => {
    if (liquidGlassEnabled) {
      document.documentElement.setAttribute("data-liquid-glass", "true");
    } else {
      document.documentElement.removeAttribute("data-liquid-glass");
    }
  }, [liquidGlassEnabled]);

  // Mobile: Ensure sidebar closes when settings opens
  useEffect(() => {
    if (showSettings && window.innerWidth <= 768) {
      setSidebarCollapsed(true);
    }
  }, [showSettings]);

  useEffect(() => {
    if (!commandCenterChatEnabled) return undefined;
    const closeSheetsOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (!sidebarCollapsed) setSidebarCollapsed(true);
      if (commandContextOpen) setCommandContextOpen(false);
    };
    document.addEventListener("keydown", closeSheetsOnEscape);
    return () => document.removeEventListener("keydown", closeSheetsOnEscape);
  }, [commandCenterChatEnabled, sidebarCollapsed, commandContextOpen]);

  // Startup sequence - check backend health before loading app
  useEffect(() => {
    initializeApp();
    // Note: Feature flags are initialized in index.js BEFORE React renders

    // PWA Offline Status Listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const initializeApp = async () => {
    try {
      // Step 1: Check backend health
      setStartupStatus("Connecting to backend...");
      setStartupProgress(25);

      let retries = 0;
      let backendReady = false;

      while (retries < 30 && !backendReady) {
        try {
          const healthCheck = await fetch(apiUrl("/api/health"), {
            method: "GET",
            signal: AbortSignal.timeout(2000),
          });

          if (healthCheck.ok) {
            const data = await healthCheck.json();

            if (data.status === "ready" || data.status === "ok") {
              backendReady = true;
              console.log("✅ Backend is ready");
            } else if (
              healthCheck.status === 503 ||
              data.status === "initializing"
            ) {
              setStartupStatus(data.message || "Server starting up...");
              console.log(`Server initializing... (attempt ${retries + 1}/30)`);
              retries++;
              await new Promise((resolve) => setTimeout(resolve, 1000));
            }
          }
        } catch (err) {
          console.log(`Waiting for backend... (attempt ${retries + 1}/30)`);
          retries++;
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (!backendReady) {
        setStartupStatus("Backend connection failed. Please restart the app.");
        return;
      }

      // Step 2: Backend ready - wait for Clerk auth
      // Don't set startupComplete here - let checkAuth() handle it when Clerk is ready
      setStartupStatus("Waiting for authentication...");
      setStartupProgress(50);
    } catch (error) {
      console.error("❌ Startup error:", error);
      setStartupStatus("Startup failed. Please refresh the page.");
    }
  };

  const checkAuth = async () => {
    console.log("🔐 checkAuth called", {
      clerkLoaded,
      isSignedIn,
      hasClerkUser: !!clerkUser,
    });

    // Use real Clerk authentication
    if (clerkLoaded) {
      if (isSignedIn && clerkUser) {
        console.log("✅ User authenticated via Clerk");
        // Preserve the RAW tier value (undefined vs "free") — the App
        // uses tierRaw to detect the "your account is still provisioning"
        // state after purchase, when tier is undefined for a moment
        // before syncClerkMetadata fires. `tier` keeps the "Free"
        // fallback so downstream gate code doesn't need to change.
        setUser({
          id: clerkUser.id,
          email:
            clerkUser.primaryEmailAddress?.emailAddress ||
            clerkUser.emailAddresses?.[0]?.emailAddress,
          name:
            clerkUser.fullName ||
            clerkUser.firstName ||
            clerkUser.emailAddresses?.[0]?.emailAddress,
          imageUrl: clerkUser.imageUrl,
          hasImage: clerkUser.hasImage, // true only if user uploaded a custom photo
          company: clerkUser.publicMetadata?.company,
          tier: clerkUser.publicMetadata?.tier || "Free",
          tierRaw: clerkUser.publicMetadata?.tier || null,
          createdAt: clerkUser.createdAt || null,
        });
        setShowSplashPage(false);
        setAuthLoading(false);
        // NOW we can show the app - auth is ready
        setStartupStatus("Ready!");
        setStartupProgress(100);
        setStartupComplete(true);
      } else {
        console.warn("❌ User not signed in via Clerk");
        // Not signed in - show auth page
        setUser(null);
        setShowSplashPage(true);
        setAuthLoading(false);
        // Still complete startup so auth page can show
        setStartupComplete(true);
      }
    }
  };

  // Check auth whenever Clerk state changes
  useEffect(() => {
    console.log("📊 Clerk state changed", { clerkLoaded, isSignedIn });
    if (clerkLoaded) {
      checkAuth();
    }
  }, [clerkLoaded, isSignedIn, clerkUser]);

  // Claude-connect status for the activation banner. Re-checks when
  // Settings toggles so the banner clears right after they connect.
  // Errors leave state at null (unknown) — never nag on a flaky check.
  useEffect(() => {
    if (!isSignedIn || !clerkUser) return;
    if (["admin", "vip"].includes(clerkUser?.publicMetadata?.role)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(apiUrl("/api/claude-max/status"));
        const data = await res.json();
        if (!cancelled) setClaudeConnected(!!data.connected);
      } catch {
        if (!cancelled) setClaudeConnected(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, clerkUser, showSettings]);

  // Timeout safety net - don't hang forever
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (authLoading) {
        console.error("⏰ Auth timeout - forcing auth page");
        setAuthLoading(false);
        setShowSplashPage(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [authLoading]);

  // Poll Harry's status from backend (C3 tasks)
  useEffect(() => {
    if (!user || !isSignedIn) return;

    // When a Harry task finishes, his answer is inserted into the conversation
    // server-side (c3Service, <!--harry:taskId--> marker). Without a refetch the
    // open chat never shows it — the user thinks Harry never answered. Pull any
    // harry-marked messages we don't have yet on the active→done transition.
    const syncHarryResultMessages = async () => {
      const convId = sessionStorage.getItem("currentConversationId");
      if (!convId) return;
      try {
        const res = await conversationApi.get(convId);
        if (res.ok === false) return;
        const data = await res.json();
        if (!data.success) return;
        const fresh = sanitizeConversationMessages(data.conversation.messages);
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id).filter(Boolean));
          const additions = fresh.filter(
            (m) =>
              m.id &&
              !seen.has(m.id) &&
              typeof m.content === "string" &&
              m.content.includes("<!--harry:"),
          );
          return additions.length ? [...prev, ...additions] : prev;
        });
      } catch (err) {
        console.debug("[HoneyBadger] Result sync failed:", err.message);
      }
    };

    const pollHarryStatus = async () => {
      try {
        const response = await authFetch(apiUrl("/api/honeybadger/status"));
        if (response.ok) {
          const data = await response.json();
          if (data.active) {
            harryHadActiveTaskRef.current = true;
            setHoneyBadgerActive(true);
            setHoneyBadgerTarget(data.goal || "Working...");
            setHoneyBadgerProgress(`${data.queueDepth || 1} task${data.queueDepth !== 1 ? 's' : ''}`);
            setHoneyBadgerComplete(data.status === 'completed');
            if (data.status === 'completed') syncHarryResultMessages();
          } else {
            // Only deactivate if we're not in an active streaming session with Harry
            if (!isHarryActive) {
              setHoneyBadgerActive(false);
            }
            if (harryHadActiveTaskRef.current) {
              harryHadActiveTaskRef.current = false;
              syncHarryResultMessages();
            }
          }
        }
      } catch (err) {
        // Silently fail - don't break the app if status endpoint is down
        console.debug("[HoneyBadger] Status poll failed:", err.message);
      }
    };

    // Initial poll
    pollHarryStatus();

    // Poll every 5 seconds
    const interval = setInterval(pollHarryStatus, 5000);

    return () => clearInterval(interval);
  }, [user, isSignedIn, isHarryActive]);

  const handleLogin = (clerkUserData) => {
    // Clerk user is already available via useUser hook
    // This handler is called from AuthPage after successful login
    setUser({
      id: clerkUserData.id,
      email:
        clerkUserData.primaryEmailAddress?.emailAddress ||
        clerkUserData.emailAddresses?.[0]?.emailAddress,
      name:
        clerkUserData.fullName ||
        clerkUserData.firstName ||
        clerkUserData.emailAddresses?.[0]?.emailAddress,
      imageUrl: clerkUserData.imageUrl,
      hasImage: clerkUserData.hasImage, // true only if user uploaded a custom photo
      company: clerkUserData.publicMetadata?.company,
      tier: clerkUserData.publicMetadata?.tier || "Free",
    });
    setShowSplashPage(false);
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    try {
      console.log("🚪 Logging out...");
      // Clear user-specific cache for security
      localStorage.removeItem("conversations_cache");
      sessionStorage.removeItem("currentConversationId");
      resetConversationExecutionState(null);
      conversationLoadRequestRef.current += 1;
      setCurrentConversationId(null);
      setMessages([]);
      // Sign out from Clerk
      await signOut();
      // Clear local state
      setUser(null);
      setShowAffiliate(false);
      setShowAutomations(false);
      setShowROIDashboard(false);
      setShowCustomDashboard(false);
      setShowSplashPage(true);
      // Redirect to auth preview to test login again
      window.location.href = "/?preview=auth";
    } catch (error) {
      console.error("❌ Logout failed:", error);
    }
  };

  // Trigger affiliate overlay on specific events
  const triggerAffiliateOverlay = (trigger) => {
    // Check if user has dismissed this trigger before
    const dismissed = JSON.parse(
      localStorage.getItem("dismissedOverlays") || "[]",
    );
    if (!dismissed.includes(trigger)) {
      setOverlayTrigger(trigger);
      setShowAffiliateOverlay(true);
    }
  };

  const dismissOverlayForever = (trigger) => {
    const dismissed = JSON.parse(
      localStorage.getItem("dismissedOverlays") || "[]",
    );
    dismissed.push(trigger);
    localStorage.setItem("dismissedOverlays", JSON.stringify(dismissed));
  };

  // Settings popup removed - users can access settings via bottom-left menu
  // Ollie can direct users there if needed ("Check Settings in the bottom left")
  // No more auto-popup on first visit - cleaner UX

  useEffect(() => {
    if (currentConversationId) {
      sessionStorage.setItem("currentConversationId", currentConversationId);
      if (messages.length === 0) {
        handleSelectConversation(currentConversationId);
      }
    } else if (user && messages.length === 0) {
      loadMostRecentConversation();
    }
  }, [currentConversationId, user, messages.length]);
  // Poll for pending turns (Harry results) every 10 seconds
  useEffect(() => {
    if (!currentConversationId || !user) return;

    const pollPendingTurns = async () => {
      if (isStreaming) return;
      try {
        const response = await authFetch(
          apiUrl(`/api/c3/pending-turns?conversationId=${encodeURIComponent(currentConversationId)}`)
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!data.turns || data.turns.length === 0) return;

        for (const turn of data.turns) {
          const turnMessage = {
            role: "assistant",
            content: turn.content,
            contentBlocks: [{ type: "text", content: turn.content }],
            timestamp: new Date(turn.timestamp).getTime(),
            source: turn.source,
            isPendingTurn: true,
          };

          setMessages((prev) => [...prev, turnMessage]);

          // Persist to conversation history
          authFetch(apiUrl("/api/messages"), {
            method: "POST",
            body: JSON.stringify({
              conversationId: currentConversationId,
              role: "assistant",
              content: turn.content,
              contentBlocks: [{ type: "text", content: turn.content }],
            }),
          }).catch(() => {});
        }
      } catch (err) {
        // Silent fail — polling is best-effort
      }
    };

    const interval = setInterval(pollPendingTurns, 10000);
    pollPendingTurns(); // Check immediately on conversation load
    return () => clearInterval(interval);
  }, [currentConversationId, user, isStreaming]);

  const [harryZapCount, setHarryZapCount] = useState(0);

  const stopStreaming = () => {
    if (abortController) {
      if (currentConversationId) {
        cancelChat(currentConversationId).catch((error) => {
          console.warn("[Chat] Server cancellation request failed", error);
        });
      }
      // Harry behavior: first zap he ignores, second zap cobra bites him
      // isHarryActive is now a state variable that persists across status message changes
      if (isHarryActive) {
        if (harryZapCount === 0) {
          // First attempt - Harry doesn't care
          setHarryZapCount(1);
          dispatchNormalizedExecution({
            type: "execution/harry_stop_attempt",
          });
          const zappedMessage = {
            role: "assistant",
            content: "⚡🦡 Harry got zapped! (He doesn't give a shit)",
            timestamp: Date.now(),
            provider: "system",
          };
          const keepGoingMessage = {
            role: "assistant",
            content:
              "🦡 Nice try. Harry don't care. Honeybadger does what he wants, but he gets results!",
            timestamp: Date.now() + 1,
            provider: "system",
          };
          setMessages((prev) => [...prev, zappedMessage, keepGoingMessage]);
          // Save zap messages to DB so they persist
          if (currentConversationId) {
            saveMessageToConversationDirect(
              zappedMessage,
              currentConversationId,
            );
            saveMessageToConversationDirect(
              keepGoingMessage,
              currentConversationId,
            );
          }
          return; // Harry keeps going
        } else {
          // Second attempt - cobra gets him, he takes a nap
          setHarryZapCount(0);
          const cobraMessage = {
            role: "assistant",
            content:
              "🐍⚡🦡 Cobra got him! Harry's taking a nap... (he'll shake it off)",
            timestamp: Date.now(),
            provider: "system",
          };
          // Preserve any progress made before stopping
          if (
            streamingContentBlocksRef.current &&
            streamingContentBlocksRef.current.length > 0
          ) {
            const partialMessage = {
              role: "assistant",
              content: "Request stopped by user",
              contentBlocks: [...streamingContentBlocksRef.current],
              timestamp: Date.now(),
              provider: "system",
              wasAborted: true,
            };
            setMessages((prev) => [...prev, partialMessage, cobraMessage]);
            // Save to DB so they persist
            if (currentConversationId) {
              saveMessageToConversationDirect(
                partialMessage,
                currentConversationId,
              );
              saveMessageToConversationDirect(
                cobraMessage,
                currentConversationId,
              );
            }
          } else {
            setMessages((prev) => [...prev, cobraMessage]);
            if (currentConversationId) {
              saveMessageToConversationDirect(
                cobraMessage,
                currentConversationId,
              );
            }
          }
          // Actually stop this time
          dispatchNormalizedExecution({
            type: "execution/stopped",
            message: "Harry was stopped",
          });
          abortController.abort();
          setAbortController(null);
          setIsStreaming(false);
          setIsLoading(false);
          setAgentStatus(null);
          setStreamingMessage(null);
          streamingContentBlocksRef.current = null;
          setIsHarryActive(false); // Reset Harry active state
          return;
        }
      }

      // Ollie can be stopped - but preserve any progress made
      dispatchNormalizedExecution({
        type: "execution/stopped",
        message: "Ollie was stopped",
      });
      abortController.abort();
      setAbortController(null);
      setIsStreaming(false);
      setIsLoading(false);
      setAgentStatus(null);

      const stoppedMessage = {
        role: "assistant",
        content: "⚡ Ollie got zapped! He's taking a nap 🦦💤",
        timestamp: Date.now(),
        provider: "system",
      };

      // Preserve any progress made before stopping
      if (
        streamingContentBlocksRef.current &&
        streamingContentBlocksRef.current.length > 0
      ) {
        const partialMessage = {
          role: "assistant",
          content: "Request stopped by user",
          contentBlocks: [...streamingContentBlocksRef.current],
          timestamp: Date.now() - 1,
          provider: "system",
          wasAborted: true,
        };
        setMessages((prev) => [...prev, partialMessage, stoppedMessage]);
        // Save to DB so they persist
        if (currentConversationId) {
          saveMessageToConversationDirect(
            partialMessage,
            currentConversationId,
          );
          saveMessageToConversationDirect(
            stoppedMessage,
            currentConversationId,
          );
        }
      } else {
        setMessages((prev) => [...prev, stoppedMessage]);
        if (currentConversationId) {
          saveMessageToConversationDirect(
            stoppedMessage,
            currentConversationId,
          );
        }
      }

      setStreamingMessage(null);
      streamingContentBlocksRef.current = null;
    }
  };
  const sendMessage = async (message) => {
    // BYOK: Key loaded from Fort Knox server-side, no frontend check needed

    // MESSAGE_QUEUEING: Track if this message gets queued (for finally block)
    let wasQueuedMessage = false;
    // True once the user turn is in the DB. A reply that dies after that
    // point (deploy restart, dropped socket) is NOT "your command was not
    // sent" — the composer must not say so (Ashley, 2026-09-21).
    let userMessagePersisted = false;

    // Reset Harry state for fresh interaction
    // This ensures zap count doesn't carry over from previous messages
    setHarryZapCount(0);
    setIsHarryActive(false);

    setIsLoading(true);
    setApiStatus("optimizing");
    setCurrentProvider(null);

    // ============================================
    // IMAGE UPLOAD: Handle structured messages with attachments
    // Message can be either:
    // - string (backwards compatible)
    // - { text: string, attachments: array } (new format with images/PDFs)
    // ============================================
    let textContent = "";
    let attachments = [];
    let displayContent = ""; // What to show in UI

    if (typeof message === "object" && message !== null) {
      // Structured message with attachments
      textContent = message.text || "";
      attachments = message.attachments || [];
      // Build display content for UI (show text + attachment indicators)
      const attachmentLabels = attachments
        .map((a) => `[📎 ${a.name}]`)
        .join(" ");
      displayContent = textContent
        ? `${textContent} \n${attachmentLabels} `
        : attachmentLabels;
    } else {
      // Plain text message (backwards compatible)
      textContent = message;
      displayContent = message;
    }

    // ============================================
    // FIX: Resolve conversationId FIRST before any operations
    // This prevents the race condition where React state hasn't updated yet
    // Use a local variable throughout, don't rely on React state
    // ============================================
    let activeConversationId = currentConversationId;
    if (!activeConversationId) {
      console.log(
        "🔄 [SEND] No conversationId - creating new conversation FIRST",
      );
      activeConversationId = await handleNewConversation();
      if (!activeConversationId) {
        console.error("❌ [SEND] Failed to create conversation, aborting send");
        setIsLoading(false);
        alert("Failed to create conversation. Please try again.");
        return;
      }
      console.log("✅ [SEND] Conversation created:", activeConversationId);
    }

    const userMessage = {
      role: "user",
      content: displayContent, // Show friendly version in UI
      timestamp: Date.now(),
      // Store attachment metadata for reference (not the full base64)
      hasAttachments: attachments.length > 0,
      attachmentCount: attachments.length,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Use activeConversationId directly, not the potentially stale state
    // Note: We save displayContent to DB, not the full base64 data (too large)
    try {
      await saveMessageToConversationDirect(userMessage, activeConversationId);
      userMessagePersisted = true;
    } catch (error) {
      setIsLoading(false);
      setApiStatus("disconnected");
      dispatchNormalizedExecution({
        type: "execution/failed",
        message: "Command could not be saved",
      });
      setMessages((prev) => prev.filter((item) => item !== userMessage));
      return false;
    }

    if (!(isStreaming && messageQueueingEnabled)) {
      dispatchObjectiveOutcome({
        type: "objective/begin",
        conversationId: activeConversationId,
        text: textContent,
      });
    }

    // Check for @apollo command anywhere in message
    const apolloMatch = textContent.match(/@apollo\s+([^@]*?)(?=@|$)/i);
    if (apolloMatch) {
      const apolloParams = apolloMatch[1].trim();
      await handleApolloCommand(apolloParams, message, activeConversationId);
      return;
    }

    // ============================================
    // ATOMS SSE Streaming request
    // All chat requests now go through ATOMS - single code path
    // Legacy /api/chat/stream endpoint removed Dec 2025
    // ============================================
    try {
      // MESSAGE_QUEUEING: If already streaming and queueing is enabled,
      // DON'T reset streaming state - we're just queueing a message
      const isQueueingMessage = isStreaming && messageQueueingEnabled;

      // Declare controller outside if block so it's accessible for fetch
      // For queued messages, use existing abortController from state
      let controller = abortController;

      if (!isQueueingMessage) {
        dispatchNormalizedExecution({
          type: "execution/begin",
          conversationId: activeConversationId,
          objective: textContent,
        });
        // Create new abort controller for primary requests
        controller = new AbortController();
        setAbortController(controller);
        setIsStreaming(true);
        setProgressMessages([]); // Clear previous progress
        // Initialize streaming message with contentBlocks for Claude Code-style rendering
        if (streamingTextEnabled) {
          streamingContentBlocksRef.current = []; // Reset ref for new message
          setStreamingMessage({
            role: "assistant",
            contentBlocks: [], // Array of { type: "text"|"tool", ... }
            timestamp: Date.now(),
            isStreaming: true,
          });
        } else {
          streamingContentBlocksRef.current = null;
          setStreamingMessage(null);
        }
        setActiveTools([]); // Clear active tools
        setCompletedTools([]); // Clear completed tools
        setAgentStatus("🦦 Ollie waking up...");
      }

      // Build request body - include attachments if present
      const requestBody = {
        message: textContent,
        conversationId: activeConversationId, // Use local var, not state
      };

      // Add attachments for image upload feature
      if (attachments.length > 0) {
        requestBody.attachments = attachments.map((a) => ({
          type: a.type, // "image" or "document"
          mediaType: a.mediaType, // "image/png", "application/pdf", etc.
          data: a.data, // base64 data
          name: a.name,
        }));
        console.log(
          `📎[SEND] Sending ${attachments.length} attachment(s) with message`,
        );
      }

      const response = await postChat(requestBody, controller.signal);

      // Check for HTTP errors BEFORE trying to read stream
      if (!response.ok) {
        const errorText = await response.text().catch(() => "Unknown error");
        console.error(
          `❌[CHAT] Server returned ${response.status}: `,
          errorText,
        );

        // BYOK: Check for missing Claude API key error
        // This triggers when byok_only is enabled and user hasn't added their key
        try {
          const errorJson = JSON.parse(errorText);
          if (errorJson.code === "NO_CLAUDE_KEY") {
            dispatchNormalizedExecution({
              type: "execution/failed",
              message: "Claude API key required",
              code: "NO_CLAUDE_KEY",
            });
            setIsLoading(false);
            setAgentStatus("");
            // Show helpful message and open Settings
            // OAuth-only doctrine (Brad): never point users at raw API
            // keys. The full connect walkthrough lives in ONE place —
            // Settings → Integrations — everything else just points there.
            const helpMessage = {
              role: "assistant",
              content:
                "🔑 **One quick step: connect your Claude account**\n\nOptimus runs on your own Claude subscription — that's what makes it unlimited with no per-message metering.\n\nI've opened **Settings → Integrations** for you — the Claude walkthrough there takes about 2 minutes. Stuck on any step? Just ask me.",
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, helpMessage]);

            // Mobile: Close sidebar and alert user
            if (window.innerWidth <= 768) {
              setSidebarCollapsed(true);
              // Small timeout to allow sidebar to close before alert
              setTimeout(() => {
                alert("⚠️ Action Required: Please add your Claude API Key in Settings to continue.");
              }, 100);
            }

            setShowSettings(true);
            return;
          }
          // Handle other specific error codes with personality
          if (errorJson.error?.includes("invalid") && errorJson.error?.includes("api")) {
            dispatchNormalizedExecution({
              type: "execution/failed",
              message: "Claude API key was rejected",
              code: "INVALID_CLAUDE_KEY",
            });
            setIsLoading(false);
            setAgentStatus("");
            const helpMessage = {
              role: "assistant",
              content:
                "**The honey badger's key doesn't fit the lock.**\n\n" +
                "Your Claude API key was rejected by Anthropic. This usually means:\n" +
                "- The key was typed incorrectly (check for extra spaces or copy/paste errors)\n" +
                "- The key has been revoked or expired\n" +
                "- **Your account isn't TIER 2** (must prepay $40+ to use API)\n\n" +
                "**Fix it:**\n" +
                "1. Go to [console.anthropic.com](https://console.anthropic.com)\n" +
                "2. Make sure you've **prepaid at least $40** (Tier 2 required)\n" +
                "3. Create a fresh API key\n" +
                "4. Paste it in Settings → Integrations → Claude",
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, helpMessage]);
            setShowSettings(true);
            return;
          }

          if (errorJson.error?.includes("rate") || errorJson.error?.includes("limit")) {
            dispatchNormalizedExecution({
              type: "execution/failed",
              message: "Anthropic rate limit reached",
              code: "RATE_LIMITED",
            });
            setIsLoading(false);
            setAgentStatus("");
            const helpMessage = {
              role: "assistant",
              content:
                "**The honey badger is moving too fast!**\n\n" +
                "You've hit Anthropic's rate limit. This happens when:\n" +
                "- Too many requests in a short time\n" +
                "- Your account tier has low rate limits\n\n" +
                "**What to do:** Wait a minute and try again.\n\n" +
                "💡 *Rate limits increase with higher tiers. Tier 2 ($40+ prepaid) has much better limits than Tier 1.*",
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, helpMessage]);
            return;
          }

          if (errorJson.error?.includes("credit") || errorJson.error?.includes("balance")) {
            dispatchNormalizedExecution({
              type: "execution/failed",
              message: "Anthropic credits required",
              code: "INSUFFICIENT_CREDITS",
            });
            setIsLoading(false);
            setAgentStatus("");
            const helpMessage = {
              role: "assistant",
              content:
                "**The honey badger's wallet is empty.**\n\n" +
                "Your Anthropic account doesn't have enough credits.\n\n" +
                "🚨 **IMPORTANT: Anthropic requires TIER 2 status to use the API.**\n\n" +
                "This means you must **PREPAY at least $40** (not just authorize - actually pay it).\n\n" +
                "**How to fix:**\n" +
                "1. Go to [console.anthropic.com/settings/billing](https://console.anthropic.com/settings/billing)\n" +
                "2. Add a payment method\n" +
                "3. **Prepay $40+ in credits** (this is not optional)\n" +
                "4. Wait a few minutes for Tier 2 to activate\n" +
                "5. Try again\n\n" +
                "*Authorizing a card is NOT enough. Anthropic requires actual prepaid balance for API access.*",
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, helpMessage]);
            return;
          }
        } catch (parseErr) {
          // Not JSON, continue with generic error
        }

        throw new Error(
          `Chat request failed (${response.status}): ${errorText.substring(0, 100)}`,
        );
      }

      // Handle SSE streaming
      let finalData = null;
      // Set when the server announces a SIGTERM (deploy) mid-stream, so the
      // cut-off message can name the cause instead of guessing.
      let restartNoticeSeen = false;

      await readChatSSE(response.body, {
        onEvent: (data) => {
              dispatchNormalizedExecution({
                type: "execution/event",
                event: data,
              });
              dispatchApprovalAttention({
                type: "workflow/event",
                event: data,
              });
              if (data.type === "server_restart") {
                // The old server instance is shutting down for a deploy. The
                // turn may still finish inside the grace window; if it does
                // not, the stream simply closes and the catch below explains.
                restartNoticeSeen = true;
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus("♻️ Optimus is restarting for an update…");
              } else if (
                data.type === "status" ||
                data.type === "thinking" ||
                data.type === "executing"
              ) {
                // Add to progress list
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content); // Also update current status
              } else if (data.type === "tool_queued") {
                // SAL: Tool detected during streaming - queued for execution
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content);
                // Add tool block in "queued" state (Claude Code style)
                if (streamingTextEnabled && data.tool) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                }
              } else if (data.type === "tool_start") {
                // SAL: Tool starting execution - update queued tool to running with INPUT
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content);
                // Update tool block to "running" state or add if not exists (Claude Code style)
                if (streamingTextEnabled && data.tool) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                }
              } else if (data.type === "tool_complete") {
                // SAL: Tool finished (success or failure)
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content);
                // Update tool block status in contentBlocks (Claude Code style)
                if (streamingTextEnabled && data.tool) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                }
              } else if (data.type === "text_delta") {
                // SAL: Streaming text chunk (progressive rendering like Claude Code)
                // Feature flag: streaming_text_updates = ON
                if (streamingTextEnabled) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                  setAgentStatus("📝 Streaming...");
                }
              } else if (data.type === "text_reset") {
                // SAL: a guard withdrew the draft that just streamed — drop it
                if (streamingTextEnabled) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                  setAgentStatus("🦦 Double-checking that…");
                }
              } else if (data.type === "text_end") {
                // SAL: Streaming complete - final text available
                // Feature flag: streaming_text_updates = ON
                if (streamingTextEnabled) {
                  // Mark streaming as complete but DON'T clear - we'll convert to final message
                  setStreamingMessage(
                    (prev) => reduceStreamingMessageForEvent(prev, data),
                  );
                  setAgentStatus("✨ Response complete");
                }
              } else if (data.type === "text") {
                // SAL: Full text blob (legacy non-streaming mode)
                // Feature flag: streaming_text_updates = OFF
                if (streamingTextEnabled) {
                  // Streaming mode but got blob - add as text block
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                  setAgentStatus("📝 Response ready");
                } else {
                  // Legacy behavior - just show status, wait for final blob
                  setAgentStatus("📝 Preparing response...");
                }
              } else if (data.type === "error") {
                // SAL: Error occurred - treat as final data to prevent "No data received"
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content);
                // Set finalData so we don't throw "No data received from stream"
                finalData = {
                  type: "error",
                  content: data.content,
                  error: { message: data.content },
                  provider: data.provider || "atoms-error",
                };
              } else if (data.type === "escalation") {
                // Harry the Honey Badger is taking over! 🦡
                setProgressMessages((prev) => [...prev, data.content]);
                setAgentStatus(data.content);
                // Set Harry active state - persists until streaming complete or stopped
                setIsHarryActive(true);
              } else if (data.type === "queued") {
                // MESSAGE_QUEUEING: Message was queued (conversation already processing)
                console.log("📨 [QUEUE] Message queued:", data.messageId);
                setQueuedMessages((prev) => [
                  ...prev,
                  {
                    id: data.messageId,
                    content: data.content,
                    queuePosition: data.queuePosition,
                    timestamp: Date.now(),
                  },
                ]);
                setProgressMessages((prev) => [
                  ...prev,
                  `📨 Message queued (position ${data.queuePosition})`,
                ]);
                // Set finalData to prevent "No data received" error
                // This is a successful queue operation, not an error
                finalData = { type: "queued", messageId: data.messageId };
              } else if (data.type === "queue_delivered") {
                // MESSAGE_QUEUEING: Queued message was delivered to agent
                console.log("✅ [QUEUE] Message delivered:", data.messageId);
                setQueuedMessages((prev) =>
                  prev.filter((m) => m.id !== data.messageId),
                );
                setProgressMessages((prev) => [
                  ...prev,
                  `✅ Queued message delivered to agent`,
                ]);
              } else if (data.type === "complete") {
                finalData = data;
                setProgressMessages((prev) => [...prev, "✅ Done!"]);
                setAgentStatus("✅ Done!");

                // Convert streaming contentBlocks to final message format if enabled
                if (streamingTextEnabled) {
                  setStreamingMessage((prev) =>
                    reduceStreamingMessageForEvent(prev, data),
                  );
                } else {
                  setStreamingMessage(null);
                }
                setActiveTools([]);
                setCompletedTools([]);
                setIsHarryActive(false); // Reset Harry active state on complete
                setHarryZapCount(0); // Reset zap count so next Harry session starts fresh

                // Update conversationId if provided in response
                if (
                  data.conversationId &&
                  data.conversationId !== currentConversationId
                ) {
                  console.log(
                    "📝 Updating conversationId from server:",
                    data.conversationId,
                  );
                  setCurrentConversationId(data.conversationId);
                  sessionStorage.setItem(
                    "currentConversationId",
                    data.conversationId,
                  );

                  // Notify sidebar to reload
                  window.dispatchEvent(new Event("conversationCreated"));
                  setTimeout(() => {
                    window.reloadSidebar?.();
                  }, 100);
                }
              }
        },
        onParseError: (error) => console.error("SSE parse error:", error),
      });

      // Handle final response
      if (!finalData) {
        // The socket closed before a final event arrived. That is a cut-off
        // reply (deploy restart on our side, or a proxy closing a long
        // connection) — never "no data", and never the user's fault.
        const cut = new Error(
          restartNoticeSeen
            ? "stream cut by server restart"
            : "stream ended before the reply finished",
        );
        cut.code = restartNoticeSeen ? "server_restart" : "stream_ended_early";
        throw cut;
      }

      // MESSAGE_QUEUEING: If this was a queued message, return early
      // The main stream is still running, don't reset state
      if (finalData.type === "queued") {
        console.log("📨 [QUEUE] Message queued successfully, preserving stream state");
        wasQueuedMessage = true; // Signal to finally block to preserve isLoading
        return; // Don't process as normal response
      }

      // Use the final data from SSE stream
      const data = finalData;
      setIsStreaming(false);
      setAgentStatus(null);
      setProgressMessages([]); // Clear progress for next request

      // Handle error case first
      if (data.error) {
        setApiStatus("disconnected");
        setCurrentProvider(null);
        const errorMessage = {
          role: "assistant",
          content: `Error: ${data.error.message || data.error} `,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        await saveMessageToConversationDirect(errorMessage, activeConversationId)
          .catch(() => undefined);
        setStreamingMessage(null);
        streamingContentBlocksRef.current = null;
        return false;
      }

      sessionStorage.removeItem("retryCount");

      if (
        data.content &&
        (data.content.includes("cannot access") ||
          data.content.includes("do not have access") ||
          data.content.includes("cannot directly access") ||
          data.content.includes("unable to"))
      ) {
        setApiStatus("disconnected");
        setCurrentProvider(null);
      } else {
        setApiStatus("connected");
        setCurrentProvider(data.provider || "claude");
      }

      let content = data.content;
      if (data.toolsUnavailable && data.claudeError) {
        content = `** Tools Unavailable **: ${data.claudeError} \n\n${content} \n\n * Note: GHL / Apollo tools require Claude API credits.* `;
      }

      setAgentStatus("📊 Formatting results...");
      await new Promise((r) => setTimeout(r, 300));
      setAgentStatus("");

      // For streaming mode: convert contentBlocks to final message, clear streamingMessage
      // This preserves tool execution history AND shows summary - NO DUPLICATION
      const hasContentBlocks =
        streamingTextEnabled &&
        streamingContentBlocksRef.current &&
        streamingContentBlocksRef.current.length > 0;

      let assistantMessage;

      if (hasContentBlocks) {
        // STREAMING MODE: Use contentBlocks that were accumulated during streaming
        // DO NOT append data.content as summary - it's the same text that was already streamed!
        const finalBlocks = [...streamingContentBlocksRef.current];

        assistantMessage = {
          role: "assistant",
          content: content, // Keep for DB storage/search
          contentBlocks: finalBlocks, // Rich rendering data (already has all text)
          timestamp: data.timestamp
            ? new Date(data.timestamp).getTime()
            : Date.now(),
          toolsUsed: data.toolsUsed || [],
          tokens: data.usage?.total_tokens || 0,
          provider: data.provider,
          model: data.model,
          latency: data.latency,
          taskType: data.taskType,
          roiData: data.roiData,
        };

        // Clear streaming state - message is now in messages[]
        setStreamingMessage(null);
        streamingContentBlocksRef.current = null;
      } else {
        // LEGACY MODE: No contentBlocks, use simple blob
        assistantMessage = {
          role: "assistant",
          content: content,
          timestamp: data.timestamp
            ? new Date(data.timestamp).getTime()
            : Date.now(),
          toolsUsed: data.toolsUsed || [],
          tokens: data.usage?.total_tokens || 0,
          provider: data.provider,
          model: data.model,
          latency: data.latency,
          taskType: data.taskType,
          roiData: data.roiData,
        };
        setStreamingMessage(null);
      }

      // Add final message to messages array (single source of truth)
      setMessages((prev) => [...prev, assistantMessage]);
      await saveMessageToConversationDirect(
        assistantMessage,
        activeConversationId,
      );
      return true;
    } catch (error) {
      // Handle abort differently than real errors
      if (error.name === "AbortError") {
        console.log("🦦 Ollie stopped by user");
        dispatchNormalizedExecution({
          type: "execution/stopped",
          message: "Execution stopped",
        });
        setIsStreaming(false);
        setAbortController(null);
        setStreamingMessage(null); // Clear streaming on abort
        streamingContentBlocksRef.current = null;
        setActiveTools([]); // Clear active tools on abort
        setCompletedTools([]); // Clear completed tools on abort
        setIsHarryActive(false); // Reset Harry active state on abort
        setHarryZapCount(0); // Reset zap count so next Harry session starts fresh
        return;
      }

      console.error("Chat error:", error);
      dispatchNormalizedExecution({
        type: "execution/failed",
        message: error.message || "Execution failed",
      });
      setApiStatus("disconnected");
      setCurrentProvider(null);
      setAgentStatus("");
      // A failed request is over: clear the streaming state exactly like the
      // abort path does. Left set, the composer stayed in its "streaming"
      // layout (Stop button lit, input collapsed to a one-character column)
      // until a page refresh — Ashley, 2026-09-08, after a 413 on a large PDF.
      setIsStreaming(false);
      setAbortController(null);
      setStreamingMessage(null);
      streamingContentBlocksRef.current = null;
      setActiveTools([]);
      setIsHarryActive(false);

      // Detect network-specific errors and provide helpful messages
      // Honey badger doesn't quit - but sometimes the network does
      let errorContent;
      const errMsg = error.message?.toLowerCase() || "";
      const errName = error.name?.toLowerCase() || "";

      if (error.code === "server_restart") {
        // We KNOW a deploy cut this reply (the server said so before it went
        // down). Ashley 2026-09-21: 4 prod deploys in 20 min, her turn died
        // with the old instance and the UI said "No data received from stream".
        errorContent =
          "**Optimus restarted mid-reply.**\n\n" +
          "A platform update landed while I was working on this and the restart cut the reply off. " +
          "Your message is saved, and anything that finished before the cut is saved too — nothing after it happened.\n\n" +
          "**Try:** send the same message again — it's back in the box. The restart takes about a minute.";
      } else if (error.code === "stream_ended_early") {
        errorContent =
          "**The reply was cut off before it finished.**\n\n" +
          "The connection to our server closed mid-reply. That is usually a deploy or restart on our end (about a minute), " +
          "sometimes a proxy closing a long connection. Your message is saved, and anything that finished before the cut is saved — nothing after it happened.\n\n" +
          "**Try:** send the same message again in a minute — it's back in the box. If it repeats, tell me and I'll log it for the team.";
      } else if (
        errName.includes("typeerror") &&
        (errMsg.includes("fetch") || errMsg.includes("network"))
      ) {
        // Network failure - couldn't reach server
        // 2026-09-06: a failed connect is ALSO what a server restart or
        // deploy looks like from the browser. Name both causes; never
        // assert the customer's network did it (Ashley, 2026-09-04).
        errorContent =
          "**The honey badger couldn't reach the server.**\n\n" +
          "The request never connected. That's usually one of two things: our server is restarting (a deploy takes about a minute), " +
          "or a firewall between you and us is blocking the streaming connection.\n\n" +
          "**Try:** wait a minute and send again. If it keeps failing, try a mobile hotspot — " +
          "if that works, your network is filtering long-lived HTTPS connections to `optimus-os-private.onrender.com` (ask IT to allow them). " +
          "If it fails on the hotspot too, it's on our side — tell me and I'll log it for the team.";
      } else if (errMsg.includes("timeout") || errName.includes("timeout")) {
        // Timeout - likely proxy buffering or firewall termination
        errorContent =
          "**The honey badger got tired of waiting.**\n\n" +
          "The connection stayed open but no data came through for too long. That can be our side (a step hung on our server) " +
          "or a proxy on your network buffering the stream.\n\n" +
          "**Try:** send again. If it repeats on a mobile hotspot, it's ours — tell me and I'll log it for the team. " +
          "If a hotspot fixes it, ask IT to disable SSL inspection / proxy buffering for streaming connections.";
      } else if (errMsg.includes("aborted") || errMsg.includes("cancelled")) {
        // Connection aborted mid-stream
        errorContent =
          "**The honey badger got disconnected mid-hunt.**\n\n" +
          "We were talking, then the connection dropped. That can be a deploy or restart on our end, " +
          "or a firewall closing what it thought was an idle connection.\n\n" +
          "**Try:** send again — anything that already finished is saved. If this keeps happening on a mobile hotspot, it's on our side — tell me and I'll log it. " +
          "If a hotspot fixes it, ask IT to raise idle timeouts for HTTPS traffic.";
      } else if (/\((4\d\d|5\d\d)\)\s*$/.test(error.message || "") || /\b(4\d\d|5\d\d)\b/.test(errMsg) && errMsg.includes("could not be saved")) {
        // The request reached OUR server and the server answered with an HTTP
        // error. That is never the user's wifi. 2026-09-05 (Ashley): a 413 on
        // saving a large tool-heavy reply was reported as "your network fights
        // dirty". Own it, keep the reply on screen, tell them what to do.
        const statusMatch = (error.message || "").match(/\((\d{3})\)/);
        const status = statusMatch ? Number(statusMatch[1]) : null;
        if (status === 413) {
          errorContent =
            "**That reply was too big to save.**\n\n" +
            "The tool output attached to it exceeded our server's size limit, so this exchange isn't stored in your history yet. " +
            "It's still here on screen. Ask me for a shorter result or a summary and that will save normally. " +
            "This is on our side, not your connection — the team has been notified.";
        } else {
          errorContent =
            `**Our server hit an error (${status || "unknown"}).**\n\n` +
            "Your message reached us — this isn't your network. Try once more in a moment; if it repeats, tell me and I'll log it for the team.";
        }
      } else {
        // Truly unclassified error. Say so plainly; do not speculate about the
        // user's network unless the error was a network error (handled above).
        errorContent = `**Something went wrong:** ${error.message}\n\n` +
          "Try again in a moment. If it keeps happening, tell me what you were doing and I'll get it to the team.";
      }

      const errorMessage = {
        role: "assistant",
        content: errorContent,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      if (activeConversationId) {
        await saveMessageToConversationDirect(errorMessage, activeConversationId)
          .catch(() => undefined);
      }
      // "interrupted": the user turn IS saved; only the reply was lost. The
      // composer restores the draft for a one-click resend but must not
      // claim the command was never sent.
      return userMessagePersisted ? "interrupted" : false;
    } finally {
      // MESSAGE_QUEUEING: Don't reset isLoading if this was a queued message
      // The original stream is still running
      if (!wasQueuedMessage) {
        setIsLoading(false);
      }
    }
  };

  const handleApolloCommand = async (
    params,
    originalMessage,
    conversationId,
  ) => {
    try {
      setApiStatus("connected");
      setCurrentProvider("apollo");

      if (params.toLowerCase().includes("search")) {
        await apolloSearch(params, originalMessage, conversationId);
      } else if (params.toLowerCase().includes("enrich")) {
        await apolloEnrich(params, originalMessage, conversationId);
      } else {
        await showApolloHelp(conversationId);
      }
    } catch (error) {
      console.error("Apollo command error:", error);
      const errorMessage = {
        role: "assistant",
        content: `Apollo Error: ${error.message} `,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      await saveMessageToConversationDirect(errorMessage, conversationId);
    } finally {
      setIsLoading(false);
    }
  };

  const apolloSearch = async (params, originalMessage, conversationId) => {
    const searchData = parseSearchParams(params);
    console.log("Apollo Search Debug:", searchData);

    const response = await authFetch(apiUrl("/api/apollo/search"), {
      method: "POST",
      body: JSON.stringify(searchData),
    });

    const data = await response.json();
    console.log("Apollo Response:", data);

    if (data.people && data.people.length > 0) {
      const resultsMessage = formatApolloResults(data, originalMessage);
      setMessages((prev) => [...prev, resultsMessage]);
      await saveMessageToConversationDirect(resultsMessage, conversationId);
    } else if (data.formatted_people && data.formatted_people.length > 0) {
      const formattedData = { ...data, people: data.formatted_people };
      const resultsMessage = formatApolloResults(
        formattedData,
        originalMessage,
      );
      setMessages((prev) => [...prev, resultsMessage]);
      await saveMessageToConversationDirect(resultsMessage, conversationId);
    } else {
      console.log("No people found in response fields:", Object.keys(data));
      const noResultsMessage = {
        role: "assistant",
        content:
          "No prospects found matching your criteria. Try different keywords or expand your search parameters.",
        timestamp: Date.now(),
        provider: "apollo",
      };
      setMessages((prev) => [...prev, noResultsMessage]);
      await saveMessageToConversationDirect(noResultsMessage, conversationId);
    }
  };

  const apolloEnrich = async (params, originalMessage, conversationId) => {
    const enrichData = parseEnrichParams(params);

    const response = await authFetch(apiUrl("/api/apollo/enrich"), {
      method: "POST",
      body: JSON.stringify(enrichData),
    });

    const data = await response.json();

    const enrichedMessage = {
      role: "assistant",
      content: formatEnrichResults(data, enrichData),
      timestamp: Date.now(),
      provider: "apollo",
      toolsUsed: ["apollo_enrich"],
    };

    setMessages((prev) => [...prev, enrichedMessage]);
    await saveMessageToConversationDirect(enrichedMessage, conversationId);
  };

  const parseSearchParams = (params) => {
    const defaults = {
      titles: ["CEO", "Founder"],
      company_sizes: ["1-10", "11-50"],
      limit: 10,
    };

    const lowerParams = params.toLowerCase();

    if (lowerParams.includes("fitness") || lowerParams.includes("coach"))
      defaults.query = "fitness coaching";
    if (lowerParams.includes("tech") || lowerParams.includes("software"))
      defaults.query = "technology software";
    if (lowerParams.includes("saas") || lowerParams.includes("founder")) {
      defaults.query = "SaaS software";
      defaults.titles = ["CEO", "Founder", "Co-founder"];
    }
    if (lowerParams.includes("marketing")) defaults.query = "marketing agency";

    const numberMatch = params.match(/(\d+)/);
    if (numberMatch) {
      defaults.limit = parseInt(numberMatch[1]);
    }

    const locationMatch = params.match(/in\s+([A-Za-z\s]+)/i);
    if (locationMatch) {
      defaults.locations = [locationMatch[1].trim()];
    }

    return defaults;
  };

  const parseEnrichParams = (params) => {
    // Remove "enrich" command word and clean trailing text after company name
    let cleanParams = params.replace(/^enrich\s+/i, "").trim();

    // Stop parsing at common trailing phrases
    cleanParams = cleanParams.replace(
      /\s+(and|then|also|please|to|for|about|with|tell|provide|analyze|get|give|show|find).*$/i,
      "",
    );

    const words = cleanParams.split(" ").filter((w) => w.length > 0);

    return {
      first_name: words[0] || "",
      last_name: words[1] || "",
      company_name: words.slice(2).join(" ") || "",
    };
  };

  const formatApolloResults = (data, originalMessage) => {
    const prospects = data.people.slice(0, 10);

    let content = `Found ${data.pagination?.total_entries || prospects.length} prospects: \n\n`;

    prospects.forEach((person, index) => {
      content += `** ${index + 1}. ${person.name}**\n`;
      content += `• ${person.title} at ${person.organization?.name || "Unknown Company"} \n`;
      content += `• Location: ${person.city}, ${person.state} \n`;
      content += `• LinkedIn: ${person.linkedin_url} \n\n`;
    });

    if (data.pagination?.total_entries > prospects.length) {
      content += `\n * Showing top ${prospects.length} of ${data.pagination.total_entries} total results * `;
    }

    return {
      role: "assistant",
      content,
      timestamp: Date.now(),
      provider: "apollo",
      toolsUsed: ["apollo_search"],
    };
  };

  const formatEnrichResults = (data, searchParams) => {
    const person = data.person;

    if (!person) {
      return `Could not enrich data for ${searchParams.first_name} ${searchParams.last_name} at ${searchParams.company_name}. Person may not exist in Apollo database.`;
    }

    let content = `** Enriched Data for ${person.name} **\n\n`;
    content += `• Title: ${person.title || "Unknown"} \n`;
    content += `• Email: ${person.email || "Not available"} \n`;
    content += `• Location: ${person.formatted_address || "Unknown"} \n`;
    content += `• LinkedIn: ${person.linkedin_url || "Not available"} \n`;

    if (person.employment_history && person.employment_history.length > 0) {
      content += `\n ** Employment History:**\n`;
      person.employment_history.slice(0, 3).forEach((job) => {
        content += `• ${job.title} at ${job.organization_name} (${job.start_date || "Unknown"}) \n`;
      });
    }

    return content;
  };

  const showApolloHelp = async (conversationId) => {
    const helpMessage = {
      role: "assistant",
      content: `** Apollo Commands:**\n\n• \`@apollo search [keywords]\` - Find prospects\n• \`@apollo enrich [name] [company]\` - Enrich contact data\n\n**Examples:**\n• \`@apollo search fitness coaches in Dallas\`\n• \`@apollo search SaaS founders\`\n• \`@apollo enrich John Smith TechCorp\`\n\n**Works anywhere in your message:**\n• \`Hey Claude, help me with this project @apollo search marketing agencies\``,
      timestamp: Date.now(),
      provider: "apollo",
    };
    setMessages((prev) => [...prev, helpMessage]);
    await saveMessageToConversationDirect(helpMessage, conversationId);
  };

  const handleSettingsClose = () => {
    setShowSettings(false);
    if (
      commandCenterEnabled &&
      commandCenterDestinationsEnabled &&
      commandDestination === "connections"
    ) {
      handleCommandNavigation("command");
    }
    // Remember that user has seen settings (don't auto-show on subsequent logins)
    localStorage.setItem("hasSeenSettingsOnboarding", "true");
  };

  const resetConversationExecutionState = (conversationId = null) => {
    // A response belongs to the conversation that started it. Abort before
    // switching identity so late stream events cannot appear in another chat.
    abortController?.abort();
    eventSource?.close?.();
    setAbortController(null);
    setEventSource(null);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingMessage(null);
    streamingContentBlocksRef.current = null;
    setAgentStatus("");
    setProgressMessages([]);
    setActiveTools([]);
    setCompletedTools([]);
    setQueuedMessages([]);
    setIsHarryActive(false);
    setHarryZapCount(0);
    setHoneyBadgerActive(false);
    setHoneyBadgerTarget("");
    setHoneyBadgerProgress("0/0");
    setHoneyBadgerComplete(false);
    dispatchNormalizedExecution({
      type: "execution/reset",
      conversationId,
    });
    dispatchApprovalAttention({
      type: "workflow/reset",
      conversationId,
    });
    dispatchObjectiveOutcome({
      type: "objective/reset",
      conversationId,
    });
  };

  const handleNewConversation = async (retryCount = 0) => {
    const maxRetries = 2;

    try {
      console.log(
        `🆕 [CONV] Creating new conversation (attempt ${retryCount + 1}/${maxRetries + 1})`,
      );

      const response = await conversationApi.create({ title: "New Chat" });

      // Check for HTTP errors BEFORE parsing JSON
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [CONV] Server error ${response.status}:`, errorText);

        // Retry on ANY HTTP error - robustness over fragility
        if (retryCount < maxRetries) {
          console.log(`🔄 [CONV] Retrying after ${response.status} error...`);
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (retryCount + 1)),
          );
          return handleNewConversation(retryCount + 1);
        }

        // Alert specific error for debugging
        if (typeof window !== 'undefined') {
          alert(`Connection Error: Server returned ${response.status}: ${errorText}\nURL: ${response.url}`);
        }
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Validate we got an ID back
      if (!data.id) {
        console.error(
          "❌ [CONV] Server returned success but no conversation ID:",
          data,
        );
        alert(`Error: Server returned success but no conversation ID. Data: ${JSON.stringify(data)}`);
        throw new Error("Server did not return conversation ID");
      }

      console.log(`✅ [CONV] Conversation created: ${data.id}`);

      resetConversationExecutionState(data.id);
      conversationLoadRequestRef.current += 1;
      setConversationLoadState({
        status: "idle",
        conversationId: data.id,
        error: null,
      });
      setCurrentConversationId(data.id);
      setMessages([]);
      sessionStorage.setItem("currentConversationId", data.id);

      window.dispatchEvent(new Event("conversationCreated"));
      setTimeout(() => {
        window.reloadSidebar?.();
      }, 100);

      return data.id;
    } catch (error) {
      console.error(
        `❌ [CONV] Failed to create conversation (attempt ${retryCount + 1}):`,
        error.message,
      );

      // Alert on network/CORS errors
      if (typeof window !== 'undefined') {
        alert(`Network Error: Failed to create conversation.\nError: ${error.message}`);
      }

      // Retry on ANY error - robustness over fragility
      if (retryCount < maxRetries) {
        console.log(`🔄 [CONV] Retrying after error: ${error.message}...`);
        await new Promise((resolve) =>
          setTimeout(resolve, 500 * (retryCount + 1)),
        );
        return handleNewConversation(retryCount + 1);
      }

      return null;
    }
  };

  const handleConversationDeleted = (deletedId) => {
    // When a conversation is deleted, don't auto-create a new one
    if (deletedId === currentConversationId) {
      resetConversationExecutionState(null);
      conversationLoadRequestRef.current += 1;
      setConversationLoadState({
        status: "idle",
        conversationId: null,
        error: null,
      });
      setCurrentConversationId(null);
      setMessages([]);
      sessionStorage.removeItem("currentConversationId");
    }
  };

  const handleSelectConversation = async (conversationId) => {
    if (conversationId === currentConversationId && messages.length > 0) {
      return;
    }
    const requestId = ++conversationLoadRequestRef.current;
    setConversationLoadState({
      status: "loading",
      conversationId,
      error: null,
    });
    try {
      const response = await conversationApi.get(conversationId);
      if (response.ok === false) {
        const loadError = new Error(
          response.status === 404
            ? "Conversation is no longer available"
            : `Conversation could not be loaded (${response.status})`,
        );
        loadError.status = response.status;
        throw loadError;
      }
      const data = await response.json();
      if (requestId !== conversationLoadRequestRef.current) return;
      if (data.success) {
      resetConversationExecutionState(conversationId);
        setCurrentConversationId(conversationId);
        // Sanitize costs to prevent .toFixed errors
        const sanitizedMessages = sanitizeConversationMessages(
          data.conversation.messages,
        );
        setMessages(sanitizedMessages);
        dispatchObjectiveOutcome({
          type: "objective/hydrate",
          conversationId,
          objective: createPersistedObjective(sanitizedMessages),
        });
        setConversationLoadState({
          status: "idle",
          conversationId,
          error: null,
        });
      } else {
        throw new Error(data.error || "Conversation could not be loaded");
      }
    } catch (error) {
      if (requestId !== conversationLoadRequestRef.current) return;
      console.error("Failed to load conversation:", error);
      if (error.status === 404 && conversationId === currentConversationId) {
        sessionStorage.removeItem("currentConversationId");
        resetConversationExecutionState(null);
        setCurrentConversationId(null);
        setMessages([]);
      }
      setConversationLoadState({
        status: "error",
        conversationId,
        error: error.message || "Conversation could not be loaded",
      });
    }
  };

  const loadMostRecentConversation = async () => {
    try {
      const response = await conversationApi.list();
      if (response.ok === false) {
        throw new Error(`Conversation history could not be loaded (${response.status})`);
      }
      const conversations = await response.json();

      if (!Array.isArray(conversations)) {
        throw new Error("Conversation history returned an invalid response");
      }
      if (conversations.length > 0) {
        const mostRecent = conversations[0];
        await handleSelectConversation(mostRecent.id);
      } else {
        // Don't auto-create when loading - user can click "New Chat" if needed
        setCurrentConversationId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Failed to auto-load conversation:", error);
    }
  };

  // ============================================
  // saveMessageToConversationDirect: Save message with explicit conversationId
  // This is the primary method - takes conversationId as parameter to avoid race conditions
  // ============================================
  const saveMessageToConversationDirect = async (message, conversationId) => {
    console.log(
      "💾 [SAVE-DIRECT] Saving message with explicit conversationId:",
      {
        conversationId,
        role: message.role,
        contentLength: message.content?.length || 0,
        timestamp: new Date().toISOString(),
      },
    );

    if (!conversationId) {
      console.error(
        "❌ [SAVE-DIRECT] No conversationId provided - this should never happen!",
      );
      return;
    }

    try {
      console.log("🌐 [SAVE-DIRECT] Making API call to save message...");
      const response = await conversationApi.saveMessage(
        conversationId,
        message,
      );

      if (!response.ok) {
        throw new Error(`Message could not be saved (${response.status})`);
      }

      const data = await response.json();
      console.log("✅ [SAVE-DIRECT] Message saved:", data);

      // If title was auto-updated (first user message), refresh sidebar
      if (data.titleUpdated) {
        console.log("📝 [SAVE-DIRECT] Title auto-updated, refreshing sidebar");
        window.dispatchEvent(new Event("conversationCreated"));
        setTimeout(() => {
          window.reloadSidebar?.();
        }, 100);
      }
    } catch (error) {
      console.error("❌ [SAVE-DIRECT] Failed to save message:", error);
      throw error;
    }
  };

  // ============================================
  // saveMessageToConversation: Legacy wrapper for backward compatibility
  // Uses React state - only use when you KNOW state is up to date
  // Prefer saveMessageToConversationDirect when possible
  // ============================================
  const saveMessageToConversation = async (message) => {
    console.log("💾 [SAVE] Attempting to save message (using state):", {
      conversationId: currentConversationId,
      role: message.role,
      contentLength: message.content?.length || 0,
      timestamp: new Date().toISOString(),
    });

    // If no conversationId in state, create one and wait
    let conversationId = currentConversationId;
    if (!conversationId) {
      console.log(
        "⚠️ [SAVE] No conversationId - creating new conversation and WAITING",
      );
      conversationId = await handleNewConversation();
      if (!conversationId) {
        console.error("❌ [SAVE] Failed to create conversation, aborting save");
        alert("Failed to create conversation. Please refresh and try again.");
        return;
      }
    }

    // Delegate to direct method
    await saveMessageToConversationDirect(message, conversationId);
  };

  // Helper kept for backward compatibility - immediately calls direct method
  const saveMessageToConversationLegacy = async (message) => {
    // Now we're guaranteed to have a conversationId
    const conversationId = currentConversationId;
    if (!conversationId) {
      console.error(
        "❌ [SAVE-LEGACY] No conversationId - should not reach here",
      );
      return;
    }

    try {
      console.log("🌐 [SAVE-LEGACY] Making API call to save message...");
      const response = await authFetch(apiUrl(`/api/messages`), {
        method: "POST",
        body: JSON.stringify({
          conversationId: conversationId,
          role: message.role,
          content: message.content,
          provider: message.provider,
          model: message.model,
          latency: message.latency,
          timestamp: message.timestamp,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("❌ [SAVE] API error:", {
          status: response.status,
          errorData,
        });
        throw new Error("Failed to save message");
      }

      const savedMessage = await response.json();
      console.log("✅ [SAVE] Message saved successfully:", savedMessage);
    } catch (error) {
      console.error("❌ [SAVE] Error in saveMessageToConversation:", {
        error: error.message,
        stack: error.stack,
        conversationId: conversationId,
      });
      // ✅ FIX: Show user-visible error
      alert("Failed to save message. Please refresh and try again.");
    }
  };

  // Show loading screen during startup
  if (!startupComplete) {
    return (
      <div
        className="app"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "24px",
          background: "var(--bg-primary)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: "400px",
            padding: "40px",
          }}
        >
          <img
            src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
            alt="Optimus OS"
            style={{ height: "64px", marginBottom: "32px" }}
            className="theme-logo"
          />
          <h2
            style={{
              color: "var(--text-primary)",
              fontSize: "24px",
              marginBottom: "16px",
              fontWeight: "600",
            }}
          >
            Starting Optimus OS
          </h2>
          <p
            style={{
              color: "var(--text-secondary)",
              fontSize: "14px",
              marginBottom: "24px",
            }}
          >
            {startupStatus}
          </p>
          <div
            style={{
              width: "100%",
              height: "4px",
              background: "var(--bg-tertiary)",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${startupProgress}%`,
                height: "100%",
                background: "var(--accent-primary)",
                transition: "width 0.3s ease",
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Wait for Clerk to load before rendering (prevents 401 errors)
  if (!clerkLoaded) {
    return (
      <div
        className="app"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--bg-primary)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <img
            src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
            alt="Optimus OS"
            style={{ height: "64px", marginBottom: "32px" }}
          />
          <p style={{ color: "var(--text-primary)" }}>
            Loading authentication...
          </p>
        </div>
      </div>
    );
  }

  // Show auth page if not signed in
  if (showSplashPage && !user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  // Show loading during auth check
  if (authLoading) {
    return (
      <div
        className="app"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--bg-primary)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <img
            src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
            alt="Optimus OS"
            style={{ height: "64px", marginBottom: "32px" }}
          />
          <p style={{ color: "var(--text-primary)" }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`app ${commandCenterEnabled ? "command-center-enabled" : ""} ${commandCenterChatEnabled ? "command-center-chat-enabled" : ""} ${commandCenterExecutionEnabled ? "command-center-execution-enabled" : ""} ${commandCenterObjectivesEnabled ? "command-center-objectives-enabled" : ""}`}
    >
      {/* Voice replies (Settings → General, off by default) — renders nothing */}
      <VoiceReplySpeaker messages={messages} conversationId={currentConversationId} />

      {/*
        Provisioning banner — shows when publicMetadata.tier is undefined
        AND the account was created recently. Covers the race where Clerk
        user.created can fire before Stripe's checkout.session.completed
        webhook writes tier. Auto-refreshes Clerk user every 8s while
        visible so the banner disappears as soon as tier lands.
      */}
      {user && !user.tierRaw && user.createdAt && (Date.now() - new Date(user.createdAt).getTime()) < 15 * 60 * 1000 && (
        <TierProvisioningBanner clerkUser={clerkUser} />
      )}

      {/* Claude-connect banner — the one mandatory setup step (BYOK OAuth).
          Hidden for admin/vip (system-key access) and once connected. The
          actual walkthrough lives in Settings → Integrations; this only
          points there. */}
      {isSignedIn &&
        claudeConnected === false &&
        !["admin", "vip"].includes(clerkUser?.publicMetadata?.role) && (
          <div
            className="hf-reveal ollie-activate-nudge"
            data-testid="ollie-activate-nudge"
            style={{
              // floating toast — kept out of the .app flex row, which
              // otherwise renders this banner as a giant empty left column.
              // Hidden while the composer's "/" skill menu is open
              // (body.slash-menu-open — see ChatInterface) so the menu's
              // rows never render underneath it.
              position: "fixed",
              bottom: "84px",
              left: 0,
              right: 0,
              margin: "0 auto",
              width: "fit-content",
              zIndex: 1200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              padding: "10px 18px",
              maxWidth: "min(92vw, 640px)",
              background: "var(--bg-secondary, #10151d)",
              border: "1px solid var(--hf-border-bright, rgba(0,191,255,0.35))",
              borderRadius: "12px",
              boxShadow: "var(--hf-shadow-2, 0 12px 32px rgba(2,6,12,0.36))",
              fontSize: "13px",
              color: "var(--text-primary, #eee)",
            }}
          >
            <span>
              ⚡ One step to activate Ollie: connect your Claude account
              (~2 minutes)
            </span>
            <button
              className="hf-lift hf-press hf-focus"
              onClick={() => setShowSettings(true)}
              style={{
                padding: "4px 14px",
                borderRadius: "8px",
                border: "1px solid rgba(0,212,255,0.6)",
                background: "rgba(0,212,255,0.15)",
                color: "inherit",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              Connect in Settings →
            </button>
          </div>
        )}

      {commandCenterEnabled && (
        <React.Suspense fallback={null}>
          <GlobalRail
            activeDestination={commandDestination}
            onNavigate={handleCommandNavigation}
            onOpenAccount={openCommandAccount}
            destinations={commandCenterDestinationsEnabled
              ? commandDestinations
              : undefined}
          />
        </React.Suspense>
      )}

      {/* Mobile Sidebar Overlay */}
      <div
        className={`mobile-sidebar-overlay ${!sidebarCollapsed ? "visible" : ""}`}
        onClick={() => setSidebarCollapsed(true)}
      />

      {commandDestination !== "operations" && <div
        className={`conversation-sidebar-container ${sidebarCollapsed ? "collapsed" : ""}`}
      >
        <ConversationSidebar
          commandCenterChatEnabled={commandCenterChatEnabled}
          currentConversationId={currentConversationId}
          onSelectConversation={(id) => {
            handleSelectConversation(id);
            // Auto-close sidebar on mobile selection
            if (window.innerWidth <= 820) {
              setSidebarCollapsed(true);
            }
          }}
          onNewConversation={() => {
            handleNewConversation();
            // Auto-close sidebar on mobile new chat
            if (window.innerWidth <= 820) {
              setSidebarCollapsed(true);
            }
          }}
          onConversationDeleted={handleConversationDeleted}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={toggleConversationLibrary}
          onOpenSettings={() => {
            setShowSettings(true);
            if (window.innerWidth <= 820) setSidebarCollapsed(true);
          }}
          user={user}
          onLogout={handleLogout}
          sidebarMode={sidebarMode}
          onSidebarModeChange={(mode) => {
            setSidebarMode(mode);
            if (commandCenterDestinationsEnabled) {
              handleCommandNavigation(mode === "files" ? "library" : "command");
            }
          }}
          onFileUseInChat={(path, isFolder) => {
            const prefix = isFolder ? "Look at the files in" : "Look at this file:";
            setChatInsertText(`${prefix} workspace:/${path}`);
            // Switch to chat mode so user sees the input
            if (sidebarMode === "files") {
              setSidebarMode("chats");
            }
          }}
        />
      </div>}
      <div className="main-container">
        {commandCenterEnabled && commandDestination !== "operations" && (
          <React.Suspense fallback={null}>
          <CommandHeader
            execution={commandCenterExecutionEnabled ? commandExecutionPresentation : null}
            objective={displayedObjective}
            isOnline={isOnline}
            isLoading={isLoading}
            isStreaming={isStreaming}
            isHarryActive={isHarryActive}
            agentStatus={agentStatus}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={toggleConversationLibrary}
            onNewConversation={handleNewConversation}
            contextOpen={commandContextOpen}
            onOpenContext={openCommandContext}
            user={user}
            onLogout={handleLogout}
            onOpenSettings={() => setShowSettings(true)}
          />
          </React.Suspense>
        )}
        {!commandCenterEnabled && (
        <div className="header-controls">
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Left Group: Toggle & New Chat */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                className="sidebar-toggle-btn"
                onClick={toggleConversationLibrary}
                aria-label="Toggle sidebar"
                title={sidebarCollapsed ? "Open Sidebar" : "Close Sidebar"}
              >
                {sidebarCollapsed ? <DoorOpen size={24} /> : <DoorClosed size={24} />}
              </button>
              <button
                className="mobile-new-chat-btn"
                onClick={() => {
                  console.log("📱 Mobile New Chat Clicked");
                  handleNewConversation().then(id => {
                    console.log("📱 New Chat Created with ID:", id);
                  }).catch(err => {
                    console.error("📱 New Chat Failed:", err);
                    alert("Error creating new chat: " + err.message);
                  });
                  if (window.innerWidth <= 768) setSidebarCollapsed(true);
                }}
                aria-label="New Chat"
              >
                <Plus size={24} />
              </button>
            </div>
            <div className="app-branding">
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                style={{ height: "32px" }}
                className="theme-logo"
              />
            </div>

            {/* Mobile Actions Menu Trigger */}
            <div className="mobile-menu-container">
              <button
                className="mobile-menu-btn"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                title="More Actions"
              >
                <MoreVertical size={24} />
              </button>

              {/* Mobile Menu Dropdown */}
              {showMobileMenu && (
                <>
                  <div
                    className="mobile-menu-backdrop"
                    onClick={() => setShowMobileMenu(false)}
                  />
                  <div className="mobile-menu-dropdown">
                    {/* Dynamic Buttons Logic reused from Desktop */}
                    {(() => {
                      const isMember = user?.tier?.toLowerCase() === "optimus";
                      const isProspect = user?.tier?.toLowerCase() === "prospect";
                      const isTimeUnlocked = new Date() >= new Date("2026-01-18T12:30:00-07:00");
                      const showButton = isMember || (isProspect && isTimeUnlocked);

                      if (showButton) {
                        return (
                          <button
                            className="mobile-menu-item"
                            onClick={() => {
                              setShowMobileMenu(false);
                              if (isMember) {
                                // Portal shares the Clerk session (same
                                // *.optimus.university root) — SSO, no
                                // login params needed.
                                window.open("https://optimus.university", "_blank");
                              } else {
                                window.open("https://buildwithoptimus.com/pay", "_blank");
                              }
                            }}
                          >
                            <span>{isMember ? "🚀 Optimus Portal" : "⭐ Join Optimus"}</span>
                          </button>
                        )
                      }
                      return null;
                    })()}

                    {automationsEnabled && (
                      <button
                        className="mobile-menu-item"
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowAutomations(true);
                        }}
                      >
                        <span>🤖 Automations</span>
                      </button>
                    )}

                    {affiliatesEnabled && (
                      <button
                        className="mobile-menu-item"
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowAffiliate(true);
                        }}
                      >
                        <span>💰 Affiliate Dashboard</span>
                      </button>
                    )}

                    <button
                      className="mobile-menu-item"
                      onClick={() => {
                        setShowMobileMenu(false);
                        setShowROIDashboard(true);
                      }}
                    >
                      <span>📈 ROI Calculator</span>
                    </button>

                    {/* Custom (same backdoor as the header's Custom menu) */}
                    {customDashboardsEnabled && (
                      <button
                        className="mobile-menu-item"
                        onClick={() => {
                          setShowMobileMenu(false);
                          setShowCustomDashboard(true);
                        }}
                      >
                        <span>📊 Custom dashboards</span>
                      </button>
                    )}
                    <button className="mobile-menu-item" onClick={() => { setShowMobileMenu(false); openSettingsTab("skills"); }}>
                      <span>🧰 Custom skills</span>
                    </button>
                    <button className="mobile-menu-item" onClick={() => { setShowMobileMenu(false); openSettingsTab("integrations", "custom-tools"); }}>
                      <span>🛠️ Custom tools</span>
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
          <div className="status-badges">
            {commandCenterEnabled && !commandContextOpen && (
              <button
                type="button"
                className="command-context-toggle"
                onClick={() => setCommandContextOpen(true)}
                aria-label="Open command context"
              >
                <PanelRightOpen size={16} aria-hidden="true" />
                Context
              </button>
            )}
            {/* Honey Badger Status Badge */}
            {honeyBadgerActive && (
              <div className="honey-badger-badge">
                <span className="hb-icon">
                  {honeyBadgerComplete ? "🦡💤" : "🦡"}
                </span>
                <span className="hb-text">{honeyBadgerTarget}</span>
                <span className="hb-progress">{honeyBadgerProgress}</span>
                <button
                  className="hb-stop-btn"
                  onClick={async () => {
                    await authFetch(apiUrl("/api/honeybadger/clear"), {
                      method: "POST",
                      body: JSON.stringify({
                        conversationId: currentConversationId,
                      }),
                    });
                    setHoneyBadgerActive(false);
                    const zapMessage = {
                      role: "system",
                      content:
                        "⚡ Honey badger zapped! (He didn't give a f***)",
                      timestamp: Date.now(),
                    };
                    setMessages((prev) => [...prev, zapMessage]);
                  }}
                  title="Zap the honey badger (won't care)"
                >
                  ⚡
                </button>
              </div>
            )}
            <InstallPrompt />
            {(() => {
              const isMember = user?.tier?.toLowerCase() === "optimus";
              const isProspect = user?.tier?.toLowerCase() === "prospect";
              const isTimeUnlocked =
                new Date() >= new Date("2026-01-18T12:30:00-07:00");

              const showButton = isMember || (isProspect && isTimeUnlocked);

              if (!showButton) return null;

              return (
                <button
                  className="roi-dashboard-btn"
                  onClick={() => {
                    if (isMember) {
                      // Portal shares the Clerk session (same
                      // *.optimus.university root) — SSO, no login
                      // params needed.
                      window.open("https://optimus.university", "_blank");
                    } else {
                      window.open("https://buildwithoptimus.com/pay", "_blank");
                    }
                  }}
                  title={
                    isMember
                      ? "Access Optimus Portal"
                      : "Join the Optimus Mastermind"
                  }
                >
                  {isMember ? "🚀 Portal" : "⭐ Join Optimus"}
                </button>
              );
            })()}
            {automationsEnabled && (
              <button
                className="roi-dashboard-btn"
                onClick={() => setShowAutomations(true)}
                title="Manage your AI automations, workflows, and scheduled tasks"
              >
                🤖 Automations
              </button>
            )}
            {affiliatesEnabled && (
              <button
                className="roi-dashboard-btn"
                onClick={() => setShowAffiliate(true)}
                title="Earn commissions by referring others to Optimus OS"
              >
                💰 Affiliate
              </button>
            )}
            <button
              className="roi-dashboard-btn"
              onClick={() => setShowROIDashboard(true)}
              title="Track time saved, tasks completed, and ROI metrics"
            >
              📈 ROI
            </button>
            {/* Custom — ONE button, hover/click panel: the backdoor to dashboards,
                skills, connections + tools (Brad 2026-09-15: fewer top buttons).
                Settings → Custom is the same list in the other direction. */}
            <CustomMenu
              items={[
                ...(customDashboardsEnabled ? [{ id: "dashboards", icon: "📊", label: "Dashboards", hint: "Widgets and reports from your own data", onSelect: () => setShowCustomDashboard(true) }] : []),
                { id: "skills", icon: "🧰", label: "Skills", hint: "Your playbooks — build, share, run with /slug", onSelect: () => openSettingsTab("skills") },
                { id: "connections", icon: "🔌", label: "Connections", hint: "Accounts your agents can act on", onSelect: () => openSettingsTab("integrations") },
                { id: "tools", icon: "🛠️", label: "Custom tools", hint: "Tools built for you from any API", onSelect: () => openSettingsTab("integrations", "custom-tools") },
              ]}
            />
            <span
              className={`status-badge ${!isOnline
                ? "error"
                : apiStatus === "connected"
                  ? "success"
                  : apiStatus === "rate-limited"
                    ? "warning"
                    : apiStatus === "optimizing"
                      ? "warning"
                      : "warning"
                }`}
            >
              {!isOnline
                ? "Offline"
                : apiStatus === "optimizing"
                  ? "Routing..."
                  : apiStatus === "connected" && currentProvider === "apollo"
                    ? "Apollo (Data)"
                    : apiStatus === "connected" && currentProvider === "gemini"
                      ? "Gemini (Fast)"
                      : apiStatus === "connected" && currentProvider === "claude"
                        ? "Claude (Tools)"
                        : apiStatus === "connected" &&
                          currentProvider === "openai"
                          ? "OpenAI (Backup)"
                          : apiStatus === "connected"
                            ? "AI Connected"
                            : apiStatus === "rate-limited"
                              ? "Rate Limited (Switching...)"
                              : "Standby"}
            </span>
            {user && (
              <UserMenu
                user={user}
                onLogout={handleLogout}
                onOpenSettings={() => {
                  setShowSettings(true);
                  if (window.innerWidth <= 768) setSidebarCollapsed(true);
                }}
              />
            )}
          </div>
        </div>
        )}
        {commandDestination === "operations" && commandCenterDestinationsEnabled && (
          <React.Suspense fallback={null}>
            <OperationsWorkspace />
          </React.Suspense>
        )}
        {commandDestination !== "operations" && commandCenterChatEnabled && conversationLoadState.status === "loading" && (
          <div className="command-conversation-notice" role="status">
            Loading command…
          </div>
        )}
        {commandDestination !== "operations" && commandCenterChatEnabled && conversationLoadState.status === "error" && (
          <div className="command-conversation-notice is-error" role="alert">
            <span>That command couldn’t load. Your current conversation and active work are unchanged.</span>
            <button
              type="button"
              onClick={() =>
                handleSelectConversation(conversationLoadState.conversationId)
              }
            >
              Retry
            </button>
          </div>
        )}
        {commandDestination !== "operations" && <ChatInterface
          messages={messages}
          onSendMessage={sendMessage}
          isLoading={isLoading}
          ghlConnected={ghlConnected}
          isStreaming={isStreaming}
          onStopStreaming={stopStreaming}
          agentStatus={agentStatus}
          progressMessages={progressMessages}
          harryZapCount={harryZapCount}
          isHarryActive={isHarryActive}
          streamingMessage={streamingMessage}
          activeTools={commandExecutionTools.active}
          completedTools={commandExecutionTools.completed}
          messageQueueingEnabled={messageQueueingEnabled}
          queuedMessages={queuedMessages}
          insertText={chatInsertText}
          onInsertTextHandled={() => setChatInsertText(null)}
          commandCenterEnabled={commandCenterEnabled}
          commandCenterChatEnabled={commandCenterChatEnabled}
        />}
      </div>

      {commandCenterEnabled && commandDestination !== "operations" && commandContextOpen && (
        <button
          type="button"
          className="command-context-overlay"
          aria-label="Close command context"
          onClick={closeCommandContext}
        />
      )}

      {commandCenterEnabled && commandDestination !== "operations" && (
        <React.Suspense fallback={null}>
        <CommandContextPanel
          execution={commandCenterExecutionEnabled ? commandExecutionPresentation : null}
          approvals={commandCenterApprovalsEnabled
            ? approvalAttentionPresentation.approvals
            : []}
          attentionItems={commandCenterAttentionEnabled
            ? approvalAttentionPresentation.attentionItems
            : []}
          objectiveData={commandCenterObjectivesEnabled
            ? objectiveOutcomePresentation.objectiveData
            : null}
          outcome={commandCenterReusableResultsEnabled
            ? reusableOutcomePresentation
            : commandCenterObjectivesEnabled
              ? objectiveOutcomePresentation.outcome
              : null}
          isOpen={commandContextOpen}
          onClose={closeCommandContext}
          isLoading={isLoading}
          isStreaming={isStreaming}
          isHarryActive={isHarryActive}
          agentStatus={agentStatus}
          progressMessages={progressMessages}
          activeTools={commandExecutionTools.active}
          completedTools={commandExecutionTools.completed}
          queuedMessages={queuedMessages}
          objective={displayedObjective}
          isModal={window.innerWidth <= 1180}
        />
        </React.Suspense>
      )}

      {showSettings && (
        <Settings
          onClose={() => { setSettingsInitialTab(null); handleSettingsClose(); }}
          initialTab={settingsInitialTab}
          onOpenDashboards={customDashboardsEnabled ? () => { setSettingsInitialTab(null); handleSettingsClose(); setShowCustomDashboard(true); } : null}
          onShowTutorial={() => {
            setShowSettings(false);
            triggerOnboarding(true); // true = replay mode
          }}
        />
      )}

      {showOnboarding && (
        <OnboardingFlow
          onComplete={hideOnboarding}
          onSkip={hideOnboarding}
          isReplay={isOnboardingReplay}
        />
      )}

      {
        showROIDashboard && (
          <ROIDashboard onClose={() => setShowROIDashboard(false)} />
        )
      }

      {
        showAutomations && (
          <AutomationDashboard onClose={() => {
            setShowAutomations(false);
            if (commandCenterDestinationsEnabled) handleCommandNavigation("command");
          }} />
        )
      }

      {
        showAffiliate && user && (
          <AffiliateDashboard
            onClose={() => setShowAffiliate(false)}
            user={user}
          />
        )
      }

      {
        showAffiliateOverlay && overlayTrigger && (
          <AffiliateOverlay
            trigger={overlayTrigger}
            onClose={() => setShowAffiliateOverlay(false)}
            onDismissForever={dismissOverlayForever}
            onNavigate={() => setShowAffiliate(true)}
          />
        )
      }

      {
        showCustomDashboard && (
          <CustomDashboard
            userId="demo-user"
            onClose={() => setShowCustomDashboard(false)}
          />
        )
      }
    </div >
  );
}

export default App;
// Cache bust 1764728771
